import { create, all } from "mathjs";

// Create MathJS instance
const math = create(all);

// Safe unit registration helper
function safeCreateUnit(name, config) {
  try {
    if (!math.Unit.UNITS[name]) {
      math.createUnit({ [name]: config });
    }
  } catch (e) {
    console.warn(`Unit '${name}' already exists or failed:`, e.message);
  }
}

// Register all relevant scientific & lab units
const customUnits = {
  ug: { definition: "1e-6 g", aliases: ["µg", "mcg"] },
  mg: { definition: "1e-3 g", aliases: [] },
  g: { definition: "1 g", aliases: [] },
  kg: { definition: "1000 g", aliases: [] },

  ul: { definition: "1e-6 l", aliases: ["µl", "mcL"] },
  ml: { definition: "1e-3 l", aliases: ["mL"] },
  l: { definition: "1 l", aliases: ["L"] },

  nmol: { definition: "1e-9 mol" },
  pmol: { definition: "1e-12 mol" },

  pcs: { definition: "1", aliases: ["unit", "ea"] },
  vial: { definition: "1", aliases: [] },
  tube: { definition: "1", aliases: [] },
  bottle: { definition: "1", aliases: [] },
  plate: { definition: "1", aliases: [] },
  box: { definition: "1", aliases: [] },
  set: { definition: "1", aliases: [] },
};

Object.entries(customUnits).forEach(([unit, config]) =>
  safeCreateUnit(unit, config)
);

/**
 * Convert value from one unit to another
 * @param {number} value
 * @param {string} fromUnit
 * @param {string} toUnit
 * @returns {number|null}
 */
export function convertUnits(value, fromUnit, toUnit) {
  try {
    return math.unit(value, fromUnit).toNumber(toUnit);
  } catch {
    return null;
  }
}

/**
 * Get all units compatible with the given unit
 * @param {string} unit - The base unit to find compatibility for
 * @returns {string[]} - Array of compatible units
 */
export function getCompatibleUnits(unit) {
  if (!unit) return [];

  try {
    // Define common laboratory unit groups with normalized units
    // These common groups ensure we always return consistent unit options for lab work
    const unitGroups = {
      // Volume measurements
      volume: [
        "L",
        "l", // Liter
        "ml",
        "mL", // Milliliter
        "ul",
        "µl",
        "mcL", // Microliter
      ],

      // Mass measurements
      mass: [
        "kg", // Kilogram
        "g", // Gram
        "mg", // Milligram
        "ug",
        "µg",
        "mcg", // Microgram
      ],

      // Count measurements (non-convertible units)
      count: [
        "pcs",
        "unit",
        "ea", // Basic count units
        "vial",
        "bottle", // Container units
        "plate",
        "tube", // Lab container units
        "set",
        "box", // Packaged units
      ],

      // Molar measurements
      molar: [
        "mol", // Mole
        "mmol", // Millimole
        "nmol", // Nanomole
        "pmol", // Picomole
      ],

      // Time measurements
      time: [
        "s",
        "min",
        "h",
        "d", // Second, minute, hour, day
      ],
    };

    // Check all groups for a match with case insensitivity
    const unitLower = unit.toLowerCase();

    // Test if the unit belongs to any of our predefined groups
    for (const [groupName, units] of Object.entries(unitGroups)) {
      if (units.some((u) => u.toLowerCase() === unitLower)) {
        // Log the match for debugging
        console.log(
          `[unitConversion] Unit '${unit}' matched group: ${groupName}`
        );
        // Return the whole group, ensuring the original unit is first
        const groupUnits = [...units];
        // Remove the original unit if it exists in the array
        const index = groupUnits.findIndex(
          (u) => u.toLowerCase() === unitLower
        );
        if (index !== -1) {
          groupUnits.splice(index, 1);
        }
        // Add the original unit at the beginning
        return [unit, ...groupUnits];
      }
    }

    // If not in predefined groups, use math.js to find compatible units
    console.log(
      `[unitConversion] Unit '${unit}' not in predefined groups, using mathjs compatibility check`
    );
    const base = math.unit(1, unit).toSI().formatUnits();
    const compatibles = Object.keys(math.Unit.UNITS).filter((u) => {
      try {
        return math.unit(1, u).toSI().formatUnits() === base && u !== unit;
      } catch {
        return false;
      }
    });

    // Always include the original unit first
    return [unit, ...compatibles];
  } catch (error) {
    console.error(
      `[unitConversion] Error finding compatible units for '${unit}':`,
      error
    );
    return [unit]; // Always return at least the original unit
  }
}

/**
 * Return the canonical unit name (e.g., 'g', 'l', etc.)
 * @param {string} unit
 * @returns {string|null}
 */
export function getUnitType(unit) {
  try {
    return math.unit(1, unit).formatUnits();
  } catch {
    return null;
  }
}

/**
 * Get all defined usable units
 * @returns {string[]}
 */
export function getAllUnits() {
  return Object.keys(math.Unit.UNITS)
    .filter((unit) => {
      try {
        math.unit(1, unit);
        return true;
      } catch {
        return false;
      }
    })
    .sort();
}

export { math };
